import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, fmtWhen } from '@/lib/officialsApi';
import { AvailabilityDot, OfficialAvatar, INDICATORS } from './StatusPill';
import { useOverrideFlow } from './ConfirmWarningsDialog';

const CandidateRow = ({ c, selected, onPick }) => (
  <button type="button" onClick={() => onPick(c)} data-testid={`candidate-${c.id}`}
    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg border transition-colors ${
      selected ? 'border-[#00247D] bg-[#00247D]/5' : 'border-transparent hover:bg-slate-50'}`}>
    <OfficialAvatar name={c.full_name} photo={c.photo_path} />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-slate-800 truncate">
        {c.full_name}{c.status === 'inactive' && <span className="ml-1.5 text-[10px] uppercase text-slate-400">inactive</span>}
      </div>
      <div className="text-xs text-slate-500 truncate">{c.reason}{c.positions?.length ? ` · ${c.positions.join(', ')}` : ''}</div>
    </div>
    <AvailabilityDot indicator={c.indicator} showLabel testId={`candidate-indicator-${c.id}`} />
    {selected && <Check className="w-4 h-4 text-[#00247D]" />}
  </button>
);

// Assign (or replace) one official on one game.
export const AssignDialog = ({ open, orgId, game, roleKey, roles, replace, onClose, onDone }) => {
  const [role, setRole] = useState(roleKey);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [pick, setPick] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const { run, dialog } = useOverrideFlow();

  useEffect(() => { if (open) { setRole(roleKey); setPick(null); setQ(''); setInstructions(''); setNotify(true); } }, [open, roleKey]);
  useEffect(() => {
    if (!open || !game || !role) return;
    setData(null);
    oapi(`/officials/games/${game.event_id}/${game.match_id}/candidates`, { orgId, query: { role_key: role, exclude_assignment_id: replace?.id } })
      .then(setData).catch(e => toast.error(e.message));
  }, [open, game, role, orgId, replace]);

  const list = useMemo(() => (data?.candidates || []).filter(c => !q || c.full_name.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const roleOptions = useMemo(() => {
    const crew = game?.crew || [];
    const extra = (roles || []).filter(r => !crew.some(c => c.role_key === r.key)).map(r => ({ role_key: r.key, label: r.label }));
    return [...crew, ...extra];
  }, [game, roles]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await run((override, ack) => oapi('/officials/assignments', { method: 'POST', orgId, body: {
        event_id: game.event_id, match_id: game.match_id, role_key: role, official_id: pick.id, instructions, notify, override,
        replace_assignment_id: replace?.id, acknowledge_finance: ack,
      } }));
      if (res) {
        toast.success(game.is_past ? `${pick.full_name} recorded on this past game · no email sent` : `${pick.full_name} assigned${notify ? ' · email sent' : ''}`);
        onDone();
      }
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-xl p-0 gap-0" data-testid="assign-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="text-[#00247D]">{replace ? `Replace ${replace.official_name}` : 'Assign official'}</DialogTitle>
            <DialogDescription>
              {game && <>{game.team_a_name} vs {game.team_b_name} · {fmtWhen(game)}{game.venue ? ` · ${game.venue}` : ''}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 py-3 space-y-3">
            <div className="flex gap-2">
              <Select value={role} onValueChange={setRole} disabled={!!replace}>
                <SelectTrigger className="w-44" data-testid="assign-role-select"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>{roleOptions.map(r => <SelectItem key={r.role_key} value={r.role_key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search officials…" className="pl-8" data-testid="assign-search-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
              {Object.keys(INDICATORS).map(k => <AvailabilityDot key={k} indicator={k} showLabel />)}
            </div>
            {game?.is_past && (
              <div className="text-xs rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-3 py-2" data-testid="assign-past-note">
                This game has already been played. {replace ? `${pick?.full_name || 'The new official'} replaces ${replace.official_name}` : 'The official is recorded'} as <b>Accepted</b> and nobody is emailed.
              </div>
            )}
            {data?.slot_warning && !replace && (
              <div className="text-xs rounded-md bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2" data-testid="assign-slot-warning">
                {data.slot_warning.message}
              </div>
            )}
            <div className="max-h-[38vh] overflow-y-auto -mx-1 px-1 space-y-0.5" data-testid="assign-candidates">
              {!data ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                : list.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">No officials found. Add officials in the Officials tab.</p>
                  : list.map(c => <CandidateRow key={c.id} c={c} selected={pick?.id === c.id} onPick={setPick} />)}
            </div>
            {pick && (
              <div className="space-y-2">
                {pick.warnings?.length > 0 && (
                  <div className="text-xs rounded-md bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2" data-testid="assign-pick-warnings">
                    {pick.warnings.map((w, i) => <div key={i}>• {w.message}</div>)}
                  </div>
                )}
                <Textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={2}
                  placeholder="Special instructions (optional), e.g. arrive 30 min early" data-testid="assign-instructions" />
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t flex items-center gap-3 bg-slate-50 rounded-b-lg">
            {game?.is_past ? <span className="mr-auto" /> : (
              <label className="flex items-center gap-2 text-sm text-slate-600 mr-auto">
                <Switch checked={notify} onCheckedChange={setNotify} data-testid="assign-notify-switch" /> Email the official
              </label>
            )}
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={!pick || saving} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="assign-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{game?.is_past ? (replace ? 'Record replacement' : 'Record official') : (replace ? 'Replace' : 'Assign')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {dialog}
    </>
  );
};

export default AssignDialog;
